/**
 * Click-to-measure atoms.
 *
 * Bridges the report (PrototypeReport.tsx — owns the finding chip) and the
 * case viewer (CaseViewer.tsx — owns the Cornerstone click events) without
 * either knowing about the other. Flow:
 *
 *   1. User clicks 📐 on a cortex finding chip
 *   2. PrototypeReport sets `measureModeAtom = {findingId, jobId, ...}`
 *   3. CaseViewer mounts a click listener on the active viewport
 *   4. User clicks on the lesion in the viewer
 *   5. CaseViewer computes voxel, calls /api/measure/lesion-segment, sets
 *      `measureResultAtom = {findingId, volume_mm3, axes_mm, ...}`
 *   6. PrototypeReport reads the result and rebuilds the inline phrase
 *   7. measureModeAtom cleared
 */

import { atom } from "jotai";

import type { Phrasing } from "../hooks/useReportQuery";

export interface MeasureRequest {
  /** Stable finding identifier so the result-handler can target the right
   * field even if the editor's text positions have shifted since dispatch. */
  findingId: string;
  /** Demo cohort job_id — sent to /api/measure/lesion-segment for source
   * resolution. */
  jobId: string;
  /** Initial position of the field text in the TipTap doc. May be stale by
   * the time the result lands; PrototypeReport reconciles via findingId. */
  fieldStart: number;
  fieldEnd: number;
  /** Cortex finding condition (e.g. "hepatobiliary/hepatic_lesion") for
   * formatting + provenance. */
  condition: string;
  /** Display base name ("Hepatic lesion") for phrase reconstruction. */
  baseName: string;
  /** Existing selection state when measure was triggered, so the rebuilt
   * phrase preserves prior attribute toggles (laterality etc). */
  selected: Record<string, string | null>;
  /** Candidate attributes for the legacy rebuildPhrase fallback (un-migrated
   * cohorts whose findings carry no engine phrasing). */
  candidates: ReadonlyArray<{ dim: string; value: string; probability: number }>;
  /** Engine-emitted phrasing for the chip's finding. When present, the
   * measurement effect joins its pre-rendered tokens (joinPhrasing — no
   * client-side clinical logic) and appends the measurement, instead of
   * re-deriving the phrase via rebuildPhrase. Null for pre-rollout cohorts. */
  phrasing?: Phrasing | null;
}

export interface MeasureResult {
  findingId: string;
  /** Volume in mL (mm³ / 1000). */
  volumeMl: number;
  /** PCA axis lengths in cm (mm / 10), sorted desc. */
  axesCm: [number, number, number];
  /** Series UID where the segmentation was rendered, for slice-link
   * preservation when rebuilding the phrase. */
  seriesUid: string;
  /** Slice index the user clicked on, for re-anchoring slice navigation. */
  sliceIndex: number;
}

/** Active measure-mode payload. Non-null = waiting for a click in the viewer. */
export const measureModeAtom = atom<MeasureRequest | null>(null);

/** Most-recent measure result. PrototypeReport reads + applies. */
export const measureResultAtom = atom<MeasureResult | null>(null);

/** Coarse loading flag for the case viewer's banner / cursor styling. */
export const measureLoadingAtom = atom<boolean>(false);

/** Coarse error flag, surfaced in the banner. */
export const measureErrorAtom = atom<string | null>(null);

/** Page-level click position for the most recent measure click. Drives a
 * small floating pin overlay so the click site is unmistakable even when
 * the segmentation contour is tiny. Cleared on Done/Cancel. */
export interface MeasureClickPin {
  pageX: number;
  pageY: number;
  /** Latest measurement to surface in the pin label. Null while loading. */
  volumeMl: number | null;
  /** Series the click landed in. Pin is hidden on slots viewing other series. */
  seriesUid: string;
  /** Slice the click landed on (used while still loading, before mask
   * slice indices are known). */
  sliceIndex: number;
  /** Slices the segmentation mask covers, derived from the API response's
   * `contours` keys. The pin is shown on every slot whose current slice is
   * in this set; empty/undefined falls back to `sliceIndex`. */
  maskSliceIndices?: number[];
}
export const measureClickPinAtom = atom<MeasureClickPin | null>(null);

/**
 * Axial-plane segmentation result for the reporter's 3D viewer. Mirrors the
 * shape of labeling's nnSegmentationResultAtom (axial fields only — measure
 * doesn't fetch companion planes). Axis lengths are computed by the viewer
 * from the rendered points, not stored here, so what's shown matches what's
 * drawn.
 */
export interface MeasureSegmentationResult {
  contours: Record<string, number[][][]>;
  spacing: [number, number, number];
  dimensions: [number, number, number];
  origin: [number, number, number];
  rowCosines: [number, number, number];
  columnCosines: [number, number, number];
  sliceDirection: [number, number, number];
  /** From API volume_mm3. */
  volumeMm3: number;
}
export const measureSegmentationResultAtom = atom<MeasureSegmentationResult | null>(null);

/** Per-finding nnInteractive session state — kept in a ref-like atom so
 * shift/ctrl refinement clicks can append to the existing interactions[]
 * and reuse the same session_id. Cleared when measureMode clears. */
export interface MeasureSession {
  sessionId: string;
  interactions: Array<{
    type: "point";
    coordinates: [number, number, number];
    label: "positive" | "negative";
  }>;
  /** Cornerstone annotation UIDs created from the most recent response —
   * removed before each refine and on session end so the overlay reflects
   * the current segmentation only. */
  contourUIDs: string[];
}
export const measureSessionAtom = atom<MeasureSession | null>(null);
